/**
 * Copyright(c) 2025 Magrabi
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information of Magrabi ("Confidential
 * Information"). You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the contract agreement you entered into with Magrabi.
 *
 * @author Shruti Kothiya
 */

import { FC } from "react";
import { cn } from "@/lib/tailwind";
import {
  getCurrencySymbolByLocale,
  getCurrencyFontClass,
  getLangFromLocale,
  getCurrencySymbol,
} from "@/apiUtils/commonUtils";
import { useParams } from "next/navigation";

export interface CurrencyIconProps {
  currency: string /** Currency code (e.g., "AED", "SAR", "د.إ", "ر.س") */;
  className?: string;
}

/**
 * CurrencyIcon - A utility component that renders the appropriate currency symbol
 * using font-based text rendering.
 */

const CurrencyIcon: FC<CurrencyIconProps> = ({ currency, className }) => {
  const { locale } = useParams();
  const language = getLangFromLocale(locale as string);

  // Try to get symbol by the specific currency code first, then fall back to locale-based symbol
  const symbol =
    getCurrencySymbol(currency, language) || getCurrencySymbolByLocale(locale as string);
  const currencyFontClass = getCurrencyFontClass(currency);

  return (
    <span className={cn(currencyFontClass, className)}>
      {symbol}
    </span>
  );
};

export default CurrencyIcon;
